import { useState } from "react";
import {
  BarChart3,
  Calculator,
  Flame,
  Home,
  Layers3,
  Menu,
  Percent,
  ShieldCheck,
  X,
} from "lucide-react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { config } from "../config";
import { useCountry } from "../context/CountryContext";
import { CountrySelector } from "../components/CountrySelector";

const nav = [
  { label: "Home", to: "/", icon: Home },
  { label: "Tips", to: "/tips", icon: Flame },
  { label: "Accumulators", to: "/accumulators", icon: Layers3 },
  { label: "Betting Sites", to: "/betting-sites", icon: BarChart3 },
  { label: "Promotions", to: "/promotions", icon: Percent },
  { label: "Tools", to: "/tools", icon: Calculator },
];

export function AppLayout() {
  const [open, setOpen] = useState(false);
  const { country } = useCountry();
  return (
    <div className="min-h-screen">
      <header className="glass sticky top-0 z-50 border-x-0 border-t-0">
        <div className="page-shell flex h-16 items-center justify-between gap-5">
          <Link
            to="/"
            className="display flex items-center gap-2 text-lg font-black"
          >
            <span className="grid h-8 w-8 place-items-center rounded-xl bg-lime text-sm">
              PC
            </span>
            <span className="hidden sm:inline">{config.siteName}</span>
          </Link>
          <nav className="hidden items-center gap-1 lg:flex">
            {nav.map(({ label, to }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-2 text-sm font-semibold transition ${isActive ? "bg-white/[.07] text-white" : "text-muted hover:text-white"}`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <div className="hidden sm:block">
              <CountrySelector compact />
            </div>
            <button
              className="focus-ring grid h-10 w-10 place-items-center rounded-xl border border-soft lg:hidden"
              aria-label="Open menu"
              onClick={() => setOpen(true)}
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>
      {open && (
        <div className="fixed inset-0 z-[70] bg-[#04442b]/98 p-5 text-white backdrop-blur-xl lg:hidden">
          <div className="flex items-center justify-between">
            <span className="display text-lg font-black">
              {config.siteName}
            </span>
            <button
              className="grid h-10 w-10 place-items-center rounded-xl border border-soft"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
            >
              <X />
            </button>
          </div>
          <div className="mt-6">
            <CountrySelector />
          </div>
          <nav className="mt-6 grid gap-2">
            {nav.map(({ label, to, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-xl border border-soft p-4 font-bold"
              >
                <Icon size={19} className="text-lime" />
                {label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
      <main>
        <Outlet />
      </main>
      <footer className="mt-24 border-t border-soft bg-[#050c09] py-12">
        <div className="page-shell grid gap-10 md:grid-cols-[1.4fr_1fr_1fr]">
          <div>
            <div className="display text-xl font-black">{config.siteName}</div>
            <p className="mt-3 max-w-md text-sm leading-6 text-muted">
              Sports picks, accumulators and bookmaker comparisons. We do not
              accept bets or hold customer funds.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white/[.04] px-3 py-2 text-xs text-muted">
              <ShieldCheck size={16} className="text-lime" />
              18+ · Betting involves risk
            </div>
          </div>
          <div>
            <div className="text-sm font-bold">Explore</div>
            <div className="mt-3 grid gap-2 text-sm text-muted">
              <Link to="/tips/performance">Tip performance</Link>
              <Link to="/compare">Compare bookmakers</Link>
              <Link to="/tools">Betting calculators</Link>
              <Link to="/responsible-gambling">Responsible gambling</Link>
            </div>
          </div>
          <div>
            <div className="text-sm font-bold">For {country.name}</div>
            <p className="mt-3 text-sm leading-6 text-muted">
              {country.responsibleGambling}
            </p>
            <p className="mt-3 text-xs text-muted">
              Affiliate disclosure: we may earn commission from qualifying
              bookmaker referrals. Editorial scores are independent.
            </p>
          </div>
        </div>
      </footer>
      <nav className="glass fixed inset-x-0 bottom-0 z-50 grid h-16 grid-cols-5 border-x-0 border-b-0 px-1 md:hidden">
        {nav.slice(0, 5).map(({ label, to, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-1 text-[10px] font-bold ${isActive ? "text-lime" : "text-muted"}`
            }
          >
            <Icon size={19} />
            <span>{label === "Betting Sites" ? "Sites" : label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
